'use client';
import { Button, Text } from '@mnd-frontend/ui/publish/components';
import { media } from '@mnd-frontend/ui/publish/utils';
import {
  DESKTOP_DIALOG_HORIZONTAL_PADDING,
  DESKTOP_DIALOG_VERTICAL_GAP,
  DESKTOP_DIALOG_VERTICAL_PADDING,
  MOBILE_DIALOG_HORIZONTAL_PADDING,
  MOBILE_DIALOG_VERTICAL_GAP,
  MOBILE_DIALOG_VERTICAL_PADDING,
  SPACING_1,
  SPACING_2,
} from '@mnd-frontend/ui/shared/tokens';
import { getMarketByLang } from '@mnd-frontend/utils';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import { useReportStaticData, useReportTranslation } from '../../hooks';

const DialogStyles = styled.div`
  position: absolute;
  top: 6rem;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.theme.colorBackgroundDialogDefault};
  --current-background: ${props => props.theme.colorBackgroundDialogDefault};
  > div {
    background: inherit;
    position: sticky;
    max-height: 75vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: ${DESKTOP_DIALOG_VERTICAL_GAP};
    height: 100%;
    width: 100%;
    overflow: hidden;
  }
  width: 35rem;
  max-width: 95vw;
  max-width: min(100%, 95vw);
  border: 0;
  padding: ${DESKTOP_DIALOG_VERTICAL_PADDING} 0;
  ${media.lessThan('phablet')} {
    padding: ${MOBILE_DIALOG_VERTICAL_PADDING} 0;
    > div {
      gap: ${MOBILE_DIALOG_VERTICAL_GAP};
    }
  }
  border-radius: ${props => props.theme.radiusDialog};
`;

const Texts = styled.div`
  padding: 0 ${DESKTOP_DIALOG_HORIZONTAL_PADDING};
  gap: ${SPACING_2};
  ${media.lessThan('phablet')} {
    gap: ${SPACING_1};
    padding: 0 ${MOBILE_DIALOG_HORIZONTAL_PADDING};
  }
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
`;

const Container = styled.aside`
  position: relative;
`;

const Content = styled.div`
  pointer-events: none;
  max-width: 100%;
  width: 100%;
  opacity: 0.3;
`;

const tryMonitorReportsForFree = (lang: string) => {
  switch (lang) {
    case 'sv':
      return 'https://www.mynewsdesk.com/se-sv/monitor-reports-trial/';
    case 'de':
      return 'https://www.mynewsdesk.com/de-de/monitor-reports-trial/';
    case 'da':
      return 'https://www.mynewsdesk.com/dk-da/monitor-reports-trial/';
    case 'no':
      return 'https://www.mynewsdesk.com/no-no/monitor-reports-trial/';
  }
  return 'https://www.mynewsdesk.com/en/monitor-reports-trial/';
};

export const LockedBenchmarking = ({ children }: { children: ReactNode }) => {
  const { lng, t } = useReportTranslation();
  const { id, brand } = useReportStaticData();

  const utm = [
    `mkt=${getMarketByLang(lng)}`,
    'utm_source=reports.mynewsdesk.com',
    'utm_campaign=try_brand_report',
    'utm_content=footer',
    `utm_term=${id}|${brand}`,
  ].join('&');
  const url = `${tryMonitorReportsForFree(lng)}?${utm}`;

  return (
    <Container>
      <Content>{children}</Content>
      <DialogStyles>
        <div>
          <Texts>
            <Text variant="headlineMedium" as="h4">
              {t('reports_locked_benchmarking_heading')}
            </Text>
            <Text variant="body" as="p">
              {t('reports_locked_benchmarking_body')}
            </Text>
            <Button href={url}>{t('reports_try_bp_banner_heading2')}</Button>
          </Texts>
        </div>
      </DialogStyles>
    </Container>
  );
};
